import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isValidSaudiPhoneNumber } from "../../../../setup/util/phoneNumber.helpers";
import PrimaryButton from "../../../../components/common/PrimaryButton";
import PartyTable from "../partyTable";
import MilestoneHeader from "../milestoneHeader";
import styles from "./styles.module.scss";
import OutlinedButton from "../../../../components/common/OutlinedButton";
import MilestoneFormNavButton from "../milestoneFormNavButton";
import AddPartyModal from "../addPartyModal";
import { useFieldArray, useFormContext } from "react-hook-form";
import BulkUploadModal, { PartyErrorsMap } from "../bulkUploadModal";

interface IStepThreeProps {
  currentStep: number;
  handleNext: () => void;
  handlePrev: () => void;
  handleSaveDraft: () => void;
  loading: boolean;
}

const StepThree = ({
  currentStep,
  handleNext,
  handlePrev,
  handleSaveDraft,
  loading,
}: IStepThreeProps) => {
  const { watch, control } = useFormContext();
  const { t } = useTranslation();

  const {
    fields: parties,
    append: addParty,
    remove: removeParty,
    update: updateParty,
  } = useFieldArray({
    control,
    name: `milestones.${watch("currentMilestoneIndex") || 0}.parties`,
    rules: { minLength: 2 },
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [partyIndex, setPartyIndex] = useState(0);
  const [mode, setMode] = useState("add");
  const [partyErrors, setPartyErrors] = useState<PartyErrorsMap>({});
  const currentMilestoneIndex = watch("currentMilestoneIndex") || 0;

  // Helper functions for consistent validation with bulkUploadModal
  const checkInternalDuplicates = (parties: any[]) => {
    const phoneNumbers = new Map<string, any[]>();
    parties.forEach((party) => {
      if (!party.phoneNumber) return;
      if (!phoneNumbers.has(party.phoneNumber)) {
        phoneNumbers.set(party.phoneNumber, []);
      }
      phoneNumbers.get(party.phoneNumber)?.push(party);
    });
    return phoneNumbers;
  };

  // Validate a single party and return any errors
  const validateParty = (party, allParties) => {
    const errors: Record<string, string> = {};

    // Check name field
    if (!party.name || party.name.length < 5) {
      errors.name = t("err_min_chars", { min: 5 });
    }

    // Check phoneNumber field
    if (!isValidSaudiPhoneNumber(party.phoneNumber)) {
      errors.phoneNumber = t("err_invalid_phone");
    } else {
      // Check for duplicate phone numbers (internal)
      const phoneNumbersMap = checkInternalDuplicates(allParties);
      const duplicates = phoneNumbersMap.get(party.phoneNumber);
      if (duplicates && duplicates.length > 1) {
        errors.phoneNumber = t("err_duplicate_phone");
      }
    }

    // Check amount field based on party type
    if (party.amount < 1) {
      errors.amount = t("err_number_min", { min: 1 });
    }

    return Object.keys(errors).length ? errors : null;
  };

  const validatedPartyIdsRef = useRef<Set<string>>(new Set());
  const lastMilestoneIndexRef = useRef<number>(currentMilestoneIndex);

  useEffect(() => {
    if (lastMilestoneIndexRef.current !== currentMilestoneIndex) {
      validatedPartyIdsRef.current = new Set();
      lastMilestoneIndexRef.current = currentMilestoneIndex;
    }

    const currentParties = watch(`milestones.${currentMilestoneIndex}.parties`);
    if (!currentParties?.length) return;

    const newErrors: PartyErrorsMap = {};
    let hasErrors = false;

    currentParties.forEach((party) => {
      const errorKey = party.rowId || party.id;
      if (validatedPartyIdsRef.current.has(errorKey)) return;

      const partyErrors = validateParty(party, currentParties);
      if (partyErrors) {
        newErrors[errorKey] = partyErrors;
        hasErrors = true;
      }
      validatedPartyIdsRef.current.add(errorKey);
    });

    if (hasErrors) {
      setPartyErrors((prev) => ({ ...prev, ...newErrors }));
    }
  }, [watch(`milestones.${currentMilestoneIndex}.parties`)]);

  // Helper function to check all duplicates and update the errors map
  const validateAllParties = (currentParties) => {
    if (!currentParties || currentParties.length === 0) return;

    // First, identify all duplicates
    const duplicatesMap = checkInternalDuplicates(currentParties);
    const duplicatePhoneGroups: Record<string, any[]> = {};

    // Group parties by phone number where there are duplicates
    duplicatesMap.forEach((parties, phoneNumber) => {
      if (parties.length > 1) {
        // Sort parties to ensure consistent ordering (keep newest last)
        const sortedParties = [...parties].sort((a, b) => {
          // Sort by id to ensure consistent ordering - earliest first
          const idA = a.id || "";
          const idB = b.id || "";
          return idA.localeCompare(idB);
        });
        duplicatePhoneGroups[phoneNumber] = sortedParties;
      }
    });

    // Update errors for each party
    const updatedErrors = { ...partyErrors };
    let hasChanges = false;

    // Clear previous duplicate errors from all parties
    currentParties.forEach((party) => {
      const errorKey = party.rowId || party.id;
      if (updatedErrors[errorKey]?.phoneNumber === t("err_duplicate_phone")) {
        if (Object.keys(updatedErrors[errorKey]).length === 1) {
          delete updatedErrors[errorKey];
        } else {
          const currentErrors = { ...updatedErrors[errorKey] };
          delete currentErrors.phoneNumber;
          updatedErrors[errorKey] = currentErrors;
        }
        hasChanges = true;
      }
    });

    // Now mark only the second party in each duplicate group with an error
    Object.values(duplicatePhoneGroups).forEach((parties) => {
      if (parties.length > 1) {
        // Get all parties except the first one
        const duplicateParties = parties.slice(1);

        // Mark each duplicate party with an error
        duplicateParties.forEach((party) => {
          const errorKey = party.rowId || party.id;
          const currentErrors = updatedErrors[errorKey] || {};

          if (
            !currentErrors.phoneNumber ||
            currentErrors.phoneNumber !== t("err_duplicate_phone")
          ) {
            currentErrors.phoneNumber = t("err_duplicate_phone");
            updatedErrors[errorKey] = currentErrors;
            hasChanges = true;
          }
        });
      }
    });

    // Update state if anything changed
    if (hasChanges) {
      setPartyErrors(updatedErrors);
    }
  };

  useEffect(() => {
    const currentParties = watch(`milestones.${currentMilestoneIndex}.parties`);
    // Check if we have parties with errors
    if (Object.keys(partyErrors).length > 0) {
      // Create a map of all valid error keys (id or rowId)
      const currentErrorKeys = new Map();
      currentParties?.forEach((party) => {
        const errorKey = party.rowId || party.id;
        currentErrorKeys.set(errorKey, party);
      });

      const updatedErrors: PartyErrorsMap = { ...partyErrors };
      let hasChanges = false;

      // Remove errors for parties that don't exist anymore
      for (const errorKey of Object.keys(updatedErrors)) {
        if (!currentErrorKeys.has(errorKey)) {
          delete updatedErrors[errorKey];
          hasChanges = true;
        }
      }

      // Re-validate existing parties to see if errors are fixed
      currentParties?.forEach((party) => {
        const errorKey = party.rowId || party.id;
        if (updatedErrors[errorKey]) {
          // Check name field
          if (updatedErrors[errorKey].name && party.name?.length >= 5) {
            delete updatedErrors[errorKey].name;
            hasChanges = true;
          }

          // Check phoneNumber field - handle invalid phone number case
          if (
            updatedErrors[errorKey].phoneNumber === t("err_invalid_phone") &&
            isValidSaudiPhoneNumber(party.phoneNumber)
          ) {
            delete updatedErrors[errorKey].phoneNumber;
            hasChanges = true;
          }

          // Note: duplicate phone errors are handled separately in validateAllParties

          // Check amount field
          if (updatedErrors[errorKey].amount && party.amount >= 1) {
            delete updatedErrors[errorKey].amount;
            hasChanges = true;
          }

          // If all field errors for this party are fixed, remove the party from errors
          if (Object.keys(updatedErrors[errorKey]).length === 0) {
            delete updatedErrors[errorKey];
            hasChanges = true;
          }
        }
      });

      // Check for duplicates after handling other errors
      validateAllParties(currentParties);

      // Update errors state if needed
      if (hasChanges) {
        // Clear all errors if there are no more errors
        if (Object.keys(updatedErrors).length === 0) {
          setPartyErrors({});
        } else {
          setPartyErrors(updatedErrors);
        }
      }
    }
  }, [currentMilestoneIndex, partyErrors, watch, parties]);

  // Whenever parties change, revalidate duplicates
  useEffect(() => {
    const currentParties = watch(`milestones.${currentMilestoneIndex}.parties`);
    if (currentParties?.length > 0) {
      validateAllParties(currentParties);
    }
  }, [watch(`milestones.${currentMilestoneIndex}.parties`)]);

  const isValid =
    parties?.length >= 1 &&
    parties?.some(
      (party) => (party as unknown as { type: string })?.type === "customer"
    ) &&
    Object.keys(partyErrors).filter((key) => key.startsWith("customer"))
      .length === 0;

  return (
    <div className={styles.stepThree}>
      <MilestoneHeader
        complexContractTitle={watch("title")}
        currentStep={currentStep}
        buyerOnly={
          !!watch(`milestones.${watch("currentMilestoneIndex") || 0}.buyerOnly`)
        }
      />
      <div className={styles.actionsRow}>
        <div className={styles.uploadButton}>
          <OutlinedButton
            onClick={() => {
              setMode("add");
              setUploadModalOpen(true);
            }}>
            <img
              src="/assets/images/svg/uploadFile.svg"
              alt="upload"
              className={styles.uploadIcon}
            />
            <p>{t("lbl_upload_csv_file")}</p>
          </OutlinedButton>
        </div>
        <div className={styles.addButton}>
          <PrimaryButton
            onClick={() => {
              const parties = watch(
                `milestones.${currentMilestoneIndex}.parties`
              );
              setMode("add");
              setPartyIndex(parties?.length);
              setModalOpen(true);
            }}>
            {t("lbl_add_party_with_plus")}
          </PrimaryButton>
        </div>
        {selectedIndices?.length > 0 && (
          <div className={styles.addButton}>
            <PrimaryButton
              onClick={() => {
                removeParty(selectedIndices);
                setSelectedIndices([]);
              }}>
              {t("lbl_remove_all_parties")}
            </PrimaryButton>
          </div>
        )}
      </div>
      <PartyTable
        resetSelectionTrigger={selectedIndices?.length === 0 ? 1 : 0}
        enableBulkSelect
        onSelectionChange={(selectedIndices) =>
          setSelectedIndices([...selectedIndices])
        }
        rows={
          watch(`milestones.${currentMilestoneIndex}.parties`)?.filter(
            (party) => party?.type === "customer"
          ) || []
        }
        columns={{
          showActions: true,
          showStatus: false,
          showRole: false,
          showFee: false,
          showTotal: false,
        }}
        getIndexByRow={(row) =>
          (
            watch(`milestones.${currentMilestoneIndex}.parties`) || []
          )?.findIndex((p) => p?.rowId === row?.rowId)
        }
        handleEdit={(index) => {
          setMode("edit");
          setPartyIndex(index);
          setModalOpen(true);
        }}
        handleDelete={(index) => {
          removeParty(index);
          // The validation will happen automatically through useEffect when parties change
        }}
        className={styles.partyTable}
        partyErrors={Object.fromEntries(
          Object.entries(partyErrors).filter(([key]) =>
            key.startsWith(`customer-`)
          )
        )}
      />
      <p className={styles.countLabel}>
        {t("lbl_parties_count_label")}{" "}
        {
          watch(`milestones.${currentMilestoneIndex}.parties`)?.filter(
            (party) => party?.type === "customer"
          )?.length
        }
      </p>
      <MilestoneFormNavButton
        disabled={!isValid || loading}
        handleNext={handleNext}
        handlePrev={handlePrev}
        handleSaveDraft={handleSaveDraft}
      />
      <AddPartyModal
        mode={mode}
        modalOpen={modalOpen}
        handleClose={() => {
          setModalOpen(false);
        }}
        handleSave={(partyData) => {
          const parties = watch(`milestones.${currentMilestoneIndex}.parties`);

          const rowPartyId = partyData?.rowId || "";

          // Check if this is an edit of an existing party
          if (parties?.some((party) => party?.rowId === rowPartyId)) {
            updateParty(
              parties?.findIndex((party) => party?.rowId === rowPartyId),
              { ...partyData }
            );
          } else {
            // Add new party
            const uId = Math.random().toString(36).substring(2, 9);
            addParty({
              ...partyData,
              id: `customer-${partyIndex}-${Date.now()}-${uId}`,
              type: currentStep === 2 ? "customer" : "provider",
            });
          }

          setModalOpen(false);
        }}
        type={currentStep === 2 ? "customer" : "provider"}
        partyIndex={partyIndex}
      />
      <BulkUploadModal
        type={currentStep === 2 ? "customer" : "provider"}
        modalOpen={uploadModalOpen}
        handleClose={() => {
          setUploadModalOpen(false);
        }}
        handleSave={(partyData, errors = {}) => {
          // Close the modal first to avoid any UI glitches
          setUploadModalOpen(false);

          // Add parties in bulk
          if (Array.isArray(partyData)) {
            partyData.forEach((party) => {
              addParty(party);
            });
          } else {
            addParty(partyData);
          }

          // Update errors
          setPartyErrors((prev) => ({
            ...prev,
            ...errors,
          }));
          // The validation will happen automatically through useEffect when parties change
        }}
      />
    </div>
  );
};

export default StepThree;
